import type {
  BorderEdge as StandaloneGradeBorderEdge,
  BorderLine as CenteringProjectionLine,
} from '../../lib/types'

export type CenteringCurvePoint = {
  x: number
  y: number
}

/**
 * With no `curve_kind`, `xm`/`ym` describe the point on the historical
 * quadratic at t=0.5. `piecewise_linear` makes that point a hard breakpoint.
 * Omitting both preserves the v1 straight-line contract.
 */
export type CenteringCurveLike = CenteringProjectionLine & {
  xm?: number
  ym?: number
}

export type CenteringCurveSegment = CenteringCurveLike &
  Required<Pick<CenteringCurveLike, 'xm' | 'ym'>>

const CURVE_EPSILON = 1e-9

export type SparseCenteringCurveRange = {
  key: 'start' | 'center' | 'end' | 'start-to-center' | 'center-to-end'
  start: number
  end: number
}

export function getSparseCenteringCurveRanges(
  curve: CenteringCurveLike,
  sourceWidth: number,
): { solid: SparseCenteringCurveRange[]; dashed: SparseCenteringCurveRange[] } | null {
  const points = sampleCenteringCurve(curve, 24)
  const curveLength = points.slice(1).reduce((total, point, index) => {
    const previous = points[index]
    return total + Math.hypot(point.x - previous.x, point.y - previous.y)
  }, 0)
  if (!finiteNumber(curveLength) || curveLength <= CURVE_EPSILON) return null

  const corner = clamp((sourceWidth * 0.05) / curveLength, 0.02, 0.18)
  const center = clamp((sourceWidth * 0.1) / curveLength, 0.04, 0.28)
  const halfCenter = center / 2
  return {
    solid: [
      { key: 'start', start: 0, end: corner },
      { key: 'center', start: 0.5 - halfCenter, end: 0.5 + halfCenter },
      { key: 'end', start: 1 - corner, end: 1 },
    ],
    dashed: [
      { key: 'start-to-center', start: corner, end: 0.5 - halfCenter },
      { key: 'center-to-end', start: 0.5 + halfCenter, end: 1 - corner },
    ],
  }
}

export function hasCenteringCurveMidpoint(
  curve: CenteringCurveLike | null | undefined,
): curve is CenteringCurveSegment {
  return Boolean(curve && finiteNumber(curve.xm) && finiteNumber(curve.ym))
}

export const centeringCurveHasMidpoint = hasCenteringCurveMidpoint

export function isPiecewiseLinearCenteringCurve(
  curve: CenteringCurveLike | null | undefined,
): curve is CenteringCurveSegment & { curve_kind: 'piecewise_linear' } {
  return curve?.curve_kind === 'piecewise_linear' && hasCenteringCurveMidpoint(curve)
}

export function isValidCenteringCurve(
  curve: CenteringCurveLike | null | undefined,
): curve is CenteringCurveLike {
  if (
    !curve ||
    ![curve.x1, curve.y1, curve.x2, curve.y2].every(finiteNumber) ||
    Math.hypot(curve.x2 - curve.x1, curve.y2 - curve.y1) <= CURVE_EPSILON
  ) {
    return false
  }

  const hasX = curve.xm !== undefined
  const hasY = curve.ym !== undefined
  if (hasX !== hasY || (hasX && (!finiteNumber(curve.xm) || !finiteNumber(curve.ym)))) {
    return false
  }
  if (
    (curve.curve_kind !== undefined && curve.curve_kind !== 'piecewise_linear') ||
    (curve.curve_kind === 'piecewise_linear' && !hasX)
  ) {
    return false
  }
  const samples = getSampledCurvePoints(curve)
  if (curve.sampled_points !== undefined && !samples) return false
  if (!samples) return true
  return (
    pointsClose(samples[0], { x: curve.x1, y: curve.y1 }) &&
    pointsClose(samples[samples.length - 1], { x: curve.x2, y: curve.y2 }) &&
    (!hasX ||
      pointsClose(evaluateSampledCurve(samples, 0.5), {
        x: curve.xm as number,
        y: curve.ym as number,
      }))
  )
}

export function getCenteringCurveMidpoint(
  curve: CenteringCurveLike,
  _side?: StandaloneGradeBorderEdge,
): CenteringCurvePoint {
  if (hasCenteringCurveMidpoint(curve)) {
    return { x: curve.xm, y: curve.ym }
  }
  const samples = getSampledCurvePoints(curve)
  if (samples) return evaluateSampledCurve(samples, 0.5)
  return {
    x: (curve.x1 + curve.x2) / 2,
    y: (curve.y1 + curve.y2) / 2,
  }
}

export function getCenteringCurveControlPoint(curve: CenteringCurveLike): CenteringCurvePoint {
  const midpoint = getCenteringCurveMidpoint(curve)
  return {
    x: 2 * midpoint.x - (curve.x1 + curve.x2) / 2,
    y: 2 * midpoint.y - (curve.y1 + curve.y2) / 2,
  }
}

export function evaluateCenteringCurve(
  curve: CenteringCurveLike,
  parameter: number,
): CenteringCurvePoint {
  if (isPiecewiseLinearCenteringCurve(curve)) {
    return evaluatePiecewiseLinearCurve(curve, parameter)
  }
  const samples = getSampledCurvePoints(curve)
  if (samples) return evaluateSampledCurve(samples, parameter)
  const control = getCenteringCurveControlPoint(curve)
  const remaining = 1 - parameter
  return {
    x:
      remaining * remaining * curve.x1 +
      2 * remaining * parameter * control.x +
      parameter * parameter * curve.x2,
    y:
      remaining * remaining * curve.y1 +
      2 * remaining * parameter * control.y +
      parameter * parameter * curve.y2,
  }
}

export function getCenteringCurveDerivative(
  curve: CenteringCurveLike,
  parameter: number,
): CenteringCurvePoint {
  if (isPiecewiseLinearCenteringCurve(curve)) {
    const start = parameter <= 0.5 ? { x: curve.x1, y: curve.y1 } : { x: curve.xm, y: curve.ym }
    const end = parameter <= 0.5 ? { x: curve.xm, y: curve.ym } : { x: curve.x2, y: curve.y2 }
    return {
      x: 2 * (end.x - start.x),
      y: 2 * (end.y - start.y),
    }
  }
  const samples = getSampledCurvePoints(curve)
  if (samples) return getSampledCurveDerivative(samples, parameter)
  const control = getCenteringCurveControlPoint(curve)
  return {
    x: 2 * (1 - parameter) * (control.x - curve.x1) + 2 * parameter * (curve.x2 - control.x),
    y: 2 * (1 - parameter) * (control.y - curve.y1) + 2 * parameter * (curve.y2 - control.y),
  }
}

export function trimCenteringCurve(
  curve: CenteringCurveLike,
  startParameter: number,
  endParameter: number,
): CenteringCurveSegment {
  const start = evaluateCenteringCurve(curve, startParameter)
  const spansPiecewiseBreakpoint =
    isPiecewiseLinearCenteringCurve(curve) &&
    0.5 >= Math.min(startParameter, endParameter) &&
    0.5 <= Math.max(startParameter, endParameter)
  const middle = evaluateCenteringCurve(
    curve,
    spansPiecewiseBreakpoint ? 0.5 : (startParameter + endParameter) / 2,
  )
  const end = evaluateCenteringCurve(curve, endParameter)
  const sampledPoints =
    getSampledCurvePoints(curve) && !isPiecewiseLinearCenteringCurve(curve)
      ? Array.from({ length: 17 }, (_, index) => {
          const point = evaluateCenteringCurve(
            curve,
            startParameter + ((endParameter - startParameter) * index) / 16,
          )
          return [point.x, point.y] as [number, number]
        })
      : undefined
  return {
    x1: start.x,
    y1: start.y,
    xm: middle.x,
    ym: middle.y,
    x2: end.x,
    y2: end.y,
    ...(curve.curve_kind ? { curve_kind: curve.curve_kind } : {}),
    ...(sampledPoints ? { sampled_points: sampledPoints } : {}),
  }
}

export function sampleCenteringCurve(
  curve: CenteringCurveLike,
  segmentCount = 16,
  startParameter = 0,
  endParameter = 1,
): CenteringCurvePoint[] {
  const segments = Math.max(1, Math.floor(segmentCount))
  return Array.from({ length: segments + 1 }, (_, index) =>
    evaluateCenteringCurve(
      curve,
      startParameter + ((endParameter - startParameter) * index) / segments,
    ),
  )
}

export function buildCenteringCurvePath(
  curve: CenteringCurveLike,
  startParameter = 0,
  endParameter = 1,
) {
  if (isPiecewiseLinearCenteringCurve(curve)) {
    const parameters = [startParameter]
    if (
      0.5 > Math.min(startParameter, endParameter) &&
      0.5 < Math.max(startParameter, endParameter)
    ) {
      parameters.push(0.5)
    }
    parameters.push(endParameter)
    return parameters
      .map((parameter, index) => {
        const point = evaluateCenteringCurve(curve, parameter)
        return `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
      })
      .join(' ')
  }
  if (getSampledCurvePoints(curve)) {
    const points = sampleCenteringCurve(curve, 24, startParameter, endParameter)
    return points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ')
  }
  const segment = trimCenteringCurve(curve, startParameter, endParameter)
  const control = getCenteringCurveControlPoint(segment)
  return `M ${segment.x1} ${segment.y1} Q ${control.x} ${control.y} ${segment.x2} ${segment.y2}`
}

/**
 * Evaluates the side at a source-pixel cross-section. Curved inputs solve the
 * quadratic axis coordinate; v1 inputs retain exact linear interpolation.
 */
export function evaluateCenteringCurveAtAxis(
  curve: CenteringCurveLike,
  side: StandaloneGradeBorderEdge,
  axisPosition: number,
): number {
  const vertical = side === 'left' || side === 'right'
  const startAxis = vertical ? curve.y1 : curve.x1
  const endAxis = vertical ? curve.y2 : curve.x2
  if (isPiecewiseLinearCenteringCurve(curve)) {
    const middleAxis = vertical ? curve.ym : curve.xm
    const parameter = solvePiecewiseLinearAxisParameter(
      startAxis,
      middleAxis,
      endAxis,
      axisPosition,
    )
    if (parameter === null) return Number.NaN
    const point = evaluateCenteringCurve(curve, parameter)
    return vertical ? point.x : point.y
  }

  const samples = getSampledCurvePoints(curve)
  if (samples) {
    const axisKey = vertical ? 'y' : 'x'
    const crossKey = vertical ? 'x' : 'y'
    const candidates: { parameter: number; value: number }[] = []
    for (let index = 0; index < samples.length - 1; index += 1) {
      const start = samples[index]
      const end = samples[index + 1]
      const delta = end[axisKey] - start[axisKey]
      if (Math.abs(delta) <= CURVE_EPSILON) continue
      const progress = (axisPosition - start[axisKey]) / delta
      if (progress < -CURVE_EPSILON || progress > 1 + CURVE_EPSILON) continue
      candidates.push({
        parameter: (index + progress) / (samples.length - 1),
        value: start[crossKey] + (end[crossKey] - start[crossKey]) * progress,
      })
    }
    if (candidates.length === 0) return Number.NaN
    const startAxis = samples[0][axisKey]
    const endAxis = samples[samples.length - 1][axisKey]
    const linearGuess =
      Math.abs(endAxis - startAxis) > CURVE_EPSILON
        ? (axisPosition - startAxis) / (endAxis - startAxis)
        : 0.5
    return candidates.reduce((closest, candidate) =>
      Math.abs(candidate.parameter - linearGuess) < Math.abs(closest.parameter - linearGuess)
        ? candidate
        : closest,
    ).value
  }
  if (!hasCenteringCurveMidpoint(curve)) {
    const delta = endAxis - startAxis
    if (Math.abs(delta) <= CURVE_EPSILON) return Number.NaN
    const parameter = (axisPosition - startAxis) / delta
    const point = evaluateCenteringCurve(curve, parameter)
    return vertical ? point.x : point.y
  }

  const middleAxis = vertical ? curve.ym : curve.xm
  const parameter = solveQuadraticAxisParameter(startAxis, middleAxis, endAxis, axisPosition)
  if (parameter === null) return Number.NaN
  const point = evaluateCenteringCurve(curve, parameter)
  return vertical ? point.x : point.y
}

export function centeringCurveXAtY(curve: CenteringCurveLike, y: number) {
  return evaluateCenteringCurveAtAxis(curve, 'left', y)
}

export function centeringCurveYAtX(curve: CenteringCurveLike, x: number) {
  return evaluateCenteringCurveAtAxis(curve, 'top', x)
}

function solveQuadraticAxisParameter(start: number, middle: number, end: number, target: number) {
  const control = 2 * middle - (start + end) / 2
  const a = start - 2 * control + end
  const b = 2 * (control - start)
  const c = start - target
  const linearGuess = Math.abs(end - start) > CURVE_EPSILON ? (target - start) / (end - start) : 0.5

  if (Math.abs(a) <= CURVE_EPSILON) {
    if (Math.abs(b) <= CURVE_EPSILON) return null
    return -c / b
  }

  const discriminant = b * b - 4 * a * c
  if (discriminant < -CURVE_EPSILON) return null
  const root = Math.sqrt(Math.max(0, discriminant))
  const candidates = [(-b - root) / (2 * a), (-b + root) / (2 * a)].filter(
    (value) => value >= -CURVE_EPSILON && value <= 1 + CURVE_EPSILON,
  )
  if (candidates.length === 0) return null
  return candidates.reduce((closest, value) =>
    Math.abs(value - linearGuess) < Math.abs(closest - linearGuess) ? value : closest,
  )
}

function evaluatePiecewiseLinearCurve(
  curve: CenteringCurveSegment,
  parameter: number,
): CenteringCurvePoint {
  if (parameter <= 0.5) {
    const progress = parameter * 2
    return {
      x: curve.x1 + (curve.xm - curve.x1) * progress,
      y: curve.y1 + (curve.ym - curve.y1) * progress,
    }
  }
  const progress = (parameter - 0.5) * 2
  return {
    x: curve.xm + (curve.x2 - curve.xm) * progress,
    y: curve.ym + (curve.y2 - curve.ym) * progress,
  }
}

function solvePiecewiseLinearAxisParameter(
  start: number,
  middle: number,
  end: number,
  target: number,
) {
  const candidates: { parameter: number; domainDistance: number }[] = []
  const addCandidate = (
    segmentStart: number,
    segmentEnd: number,
    parameterStart: number,
    parameterEnd: number,
  ) => {
    const delta = segmentEnd - segmentStart
    if (Math.abs(delta) <= CURVE_EPSILON) return
    const progress = (target - segmentStart) / delta
    const parameter = parameterStart + (parameterEnd - parameterStart) * progress
    const domainDistance =
      parameter < parameterStart
        ? parameterStart - parameter
        : parameter > parameterEnd
          ? parameter - parameterEnd
          : 0
    candidates.push({ parameter, domainDistance })
  }
  addCandidate(start, middle, 0, 0.5)
  addCandidate(middle, end, 0.5, 1)
  if (candidates.length === 0) return null

  const linearGuess = Math.abs(end - start) > CURVE_EPSILON ? (target - start) / (end - start) : 0.5
  return candidates.reduce((closest, candidate) => {
    if (candidate.domainDistance < closest.domainDistance - CURVE_EPSILON) return candidate
    if (candidate.domainDistance > closest.domainDistance + CURVE_EPSILON) return closest
    return Math.abs(candidate.parameter - linearGuess) < Math.abs(closest.parameter - linearGuess)
      ? candidate
      : closest
  }).parameter
}

function finiteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum)
}

function getSampledCurvePoints(curve: CenteringCurveLike): CenteringCurvePoint[] | null {
  const samples = curve.sampled_points
  if (
    !Array.isArray(samples) ||
    samples.length < 2 ||
    !samples.every(
      (point) =>
        Array.isArray(point) &&
        point.length === 2 &&
        finiteNumber(point[0]) &&
        finiteNumber(point[1]),
    )
  ) {
    return null
  }
  return samples.map(([x, y]) => ({ x, y }))
}

function evaluateSampledCurve(points: readonly CenteringCurvePoint[], parameter: number) {
  const scaled = parameter * (points.length - 1)
  const index = Math.min(Math.max(Math.floor(scaled), 0), points.length - 2)
  const progress = scaled - index
  return {
    x: points[index].x + (points[index + 1].x - points[index].x) * progress,
    y: points[index].y + (points[index + 1].y - points[index].y) * progress,
  }
}

function getSampledCurveDerivative(points: readonly CenteringCurvePoint[], parameter: number) {
  const scaled = parameter * (points.length - 1)
  const index = Math.min(Math.max(Math.floor(scaled), 0), points.length - 2)
  return {
    x: (points[index + 1].x - points[index].x) * (points.length - 1),
    y: (points[index + 1].y - points[index].y) * (points.length - 1),
  }
}

function pointsClose(first: CenteringCurvePoint, second: CenteringCurvePoint) {
  return Math.hypot(first.x - second.x, first.y - second.y) <= 1e-6
}
